import { useEffect, useState } from 'react';

function TabButton({
  tab,
  isActive,
  isDisabled,
  badgeValue,
  onSelect,
  testId,
  mobile,
}) {
  const baseClasses = mobile
    ? `flex w-full items-start gap-3 border-b px-4 py-4 text-left transition-all duration-150 ${
        isActive
          ? 'bg-slate-900 text-white border-slate-900 shadow-sm'
          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-100'
      }`
    : `group flex min-w-[180px] flex-col rounded-t-lg px-4 py-3 text-left transition-all duration-150 border ${
        isActive
          ? 'bg-slate-900 text-white border-slate-900 shadow-md'
          : 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200 hover:text-slate-900'
      }`;

  return (
    <button
      key={tab.key}
      type="button"
      disabled={isDisabled}
      onClick={() => (!isDisabled ? onSelect(tab.key) : null)}
      data-testid={testId}
      className={`${baseClasses} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      role="tab"
      aria-selected={isActive}
    >
      <div className={mobile ? 'mt-0.5' : 'hidden'}>
        <span className={isActive ? 'text-slate-100' : 'text-slate-500'}>
          {tab.icon}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <span
            className={`${isActive ? 'text-slate-100' : 'text-slate-500'} ${mobile ? 'hidden' : ''}`}
          >
            {tab.icon}
          </span>
          <span>{tab.label}</span>
          {badgeValue !== null ? (
            <span
              className={`rounded-md px-2 py-0.5 text-xs font-bold ${
                isActive
                  ? 'bg-white/20 text-white'
                  : 'bg-slate-300 text-slate-800'
              }`}
            >
              {badgeValue}
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function DesktopViewTabs(props) {
  const {
    viewTabs,
    selectedView,
    activeEvents,
    onSelect,
    viewKeys,
    tabTestIds,
  } = props;

  return (
    <div
      className="flex gap-2 overflow-x-auto pb-1"
      role="tablist"
      aria-label="Planner views"
    >
      {viewTabs.map((tab) => {
        const isActive = !tab.comingSoon && selectedView === tab.key;
        const rawBadgeValue =
          activeEvents && viewKeys.includes(tab.key)
            ? activeEvents[tab.key]
            : null;
        const badgeValue =
          typeof rawBadgeValue === 'number' ? rawBadgeValue : null;

        return (
          <TabButton
            key={tab.key}
            tab={tab}
            isActive={isActive}
            isDisabled={tab.comingSoon}
            badgeValue={badgeValue}
            onSelect={onSelect}
            testId={viewKeys.includes(tab.key) ? tabTestIds[tab.key] : null}
            mobile={false}
          />
        );
      })}
    </div>
  );
}

function MobileViewTabs(props) {
  const {
    viewTabs,
    selectedView,
    activeEvents,
    onSelect,
    viewKeys,
    tabTestIds,
  } = props;

  return (
    <div
      className="border-y border-slate-300 bg-white"
      role="tablist"
      aria-label="Planner views"
    >
      {viewTabs.map((tab) => {
        const isActive = !tab.comingSoon && selectedView === tab.key;
        const rawBadgeValue =
          activeEvents && viewKeys.includes(tab.key)
            ? activeEvents[tab.key]
            : null;
        const badgeValue =
          typeof rawBadgeValue === 'number' ? rawBadgeValue : null;

        return (
          <TabButton
            key={tab.key}
            tab={tab}
            isActive={isActive}
            isDisabled={tab.comingSoon}
            badgeValue={badgeValue}
            onSelect={onSelect}
            testId={viewKeys.includes(tab.key) ? tabTestIds[tab.key] : null}
            mobile={true}
          />
        );
      })}
    </div>
  );
}

export default function ViewTabs(props) {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= 768;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const update = (event) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', update);
      return () => mediaQuery.removeEventListener('change', update);
    }

    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, []);

  return isDesktop ? (
    <DesktopViewTabs {...props} />
  ) : (
    <MobileViewTabs {...props} />
  );
}
